<script setup>
import TD from "@/Components/Table/Element.vue";
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Textarea from "@/Components/Textarea.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import VueTimepicker from "vue3-timepicker";
import { ref, onUnmounted } from "vue";
import { Inertia } from "@inertiajs/inertia";
import MultiSelect from "@/Components/MultiSelect.vue";

const page_name = "Sondertermine";

const canDelete = Inertia.page.props.auth.can["delete settings"];

const isLoading = ref(false);

const props = defineProps({
    errors: Object,
    practitioners: Array,
    interval: Number,
    appointment: Object,
});

const repetition_date = ref(null);
const repetition_days = ref(null);
const form = useForm({
    practitioner_id: props.practitioners
        .filter((p) => p.selected)
        .map((p) => p.id),
    date: props.appointment.date,
    start: props.appointment.start,
    duration: props.appointment.duration,
    repetition: props.appointment.repetition, // once, daily, weekly, monthly
    repetition_mode: props.appointment.repetition_mode, // always, amount, date
    repetition_value_amount: props.appointment.repetition_value_amount,
    repetition_value_date: props.appointment.repetition_value_date,
    description: props.appointment.description,
    name: props.appointment.name,
});
let saveBtnClicked = false;

//onUnmounted(
//    Inertia.on("before", (e) => {
//        console.log(form.isDirty, !saveBtnClicked);
//        if (form.isDirty && !saveBtnClicked) {
//            return confirm(
//                "Sie haben ungespeicherte Eingaben. Wollen Sie diese Seite wirklich verlassen?"
//            );
//        }
//    })
//);

function deleteAppointment() {
    Inertia.delete(`/specialAppointments/${props.appointment.id}`, {});
}

function update() {
    isLoading.value = true;
    saveBtnClicked = true;

    form.put("/specialAppointments/" + props.appointment.id, {
        onFinish: () => {
            isLoading.value = false;
        },
    });
}
</script>

<template>
    <SettingsLayout>
        <!-- <Head title="Stammdaten - Grundeinstellungen" /> -->
        <Head :title="`${page_name} - Grundeinstellungen`" />

        <template #breadcrumb>{{ page_name }}</template>
        <template #controls>
            <HeaderLink
                class="mr-3"
                href="/einstellungen/sondertermine"
                type="close"
                >Zurück</HeaderLink
            >
            <HeaderButton
                v-if="canDelete"
                class="mr-3"
                @click="deleteAppointment()"
                type="close"
                >Löschen</HeaderButton
            >
            <HeaderButton :disabled="isLoading" @click="update()"
                >Termin ändern</HeaderButton
            >
        </template>
        <Heading title="Sondertermin editieren" />

        <p class="max-w-2xl text-sm text-oteoText">
            Sie können ganz einfach Sondertermine wie beispielsweise
            Teambesprechungen oder Schulungen anlegen. Bei Bedarf können Sie
            auch Wiederholungen definieren. Sondertermine werden in Ihrer
            Kalenderansicht schraffiert dargestellt. oteo ist so voreingestellt,
            dass Sie über Sondertermine auch Patiententermine vergeben können.
        </p>
        <div class="grid grid-cols-3 gap-8 mt-8">
            <div class="">
                <Label> Terminbezeichnung </Label>
                <Input v-model="form.name" class="w-full" type="text" />

                <div class="mt-2 text-red-600 text-sm" v-if="errors.name">
                    {{ errors.name }}
                </div>
            </div>
            <div class="">
                <Label> Datum </Label>

                <Input v-model="form.date" class="w-full" type="date" />
                <div class="mt-2 text-red-600 text-sm" v-if="errors.date">
                    {{ errors.date }}
                </div>
            </div>
            <div class="row-span-3 flex flex-col">
                <Label> Beschreibung </Label>
                <Textarea
                    placeholder="Bitte eintragen"
                    class="w-full h-full resize-none"
                    v-model="form.description"
                />
                <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.description"
                >
                    {{ errors.description }}
                </div>
            </div>
            <div class="">
                <Label> Teilnehmer </Label>

                <MultiSelect
                    @valueChange="(e) => (form.practitioner_id = e)"
                    :options="
                        practitioners.map((p) => {
                            return {
                                value: p.id,
                                text: p.firstname + ' ' + p.lastname,
                                label: p.username,
                                selected: p.selected,
                            };
                        })
                    "
                />

                <!-- <Select
          id="practitioner_id"
          class="mt-1 block w-full"
          v-model="form.practitioner_id"
          required
        >
          <option disabled value="">Behandler/in auswählen</option>

          <option
            v-for="practitioner in practitioners"
            :key="practitioner.id"
            :value="practitioner.id"
          >
            {{ practitioner.fullname }}
          </option>
        </Select> -->

                <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.practitioner_id"
                >
                    {{ errors.practitioner_id }}
                </div>
            </div>
            <div class="">
                <Label> Wiederholung(bei Terminserien) </Label>

                <Select v-model="form.repetition" class="w-full">
                    <option value="once">einmalig</option>
                    <option value="daily">täglich</option>
                    <option value="weekly">wöchentlich</option>
                    <option value="monthly">monatlich</option>
                </Select>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.repetition">
                    {{ errors.repetition }}
                </div>
            </div>
            <div class="">
                <Label> Uhrzeit </Label>

                <VueTimepicker
                    class="timepicker-input"
                    v-model="form.start"
                    :key="form.start"
                    :minute-interval="props.interval"
                    format="HH:mm"
                    close-on-complete
                    fixed-dropdown-button
                >
                    <template v-slot:dropdownButton>
                        <img :src="'/storage/icons/clock.png'" />
                    </template>
                </VueTimepicker>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.time">
                    {{ errors.time }}
                </div>
            </div>
            <div
                class="grid row-span-2 max-w-3xl gap-5 mt-8 text-sm text-oteoText relative"
                :class="{ 'opacity-50': form.repetition === 'once' }"
            >
                <div class="col-span-1">
                    <div class="flex items-center">
                        <input
                            :disabled="form.repetition === 'once'"
                            class="mr-4"
                            type="radio"
                            v-model="form.repetition_mode"
                            value="always"
                        />
                        Kein Enddatum
                    </div>
                    <div class="flex items-center my-4">
                        <div class="flex items-center w-32">
                            <input
                                v-model="form.repetition_mode"
                                value="amount"
                                class="mr-4"
                                type="radio"
                            />
                            Endet nach:
                        </div>
                        <Input
                            :disabled="
                                form.repetition === 'once' ||
                                form.repetition_mode !== 'amount'
                            "
                            min="2"
                            max="50"
                            step="1"
                            v-model="form.repetition_value_amount"
                            class="mr-4 w-20"
                            type="number"
                        />
                        Terminen
                    </div>
                    <div class="flex items-center">
                        <div class="flex items-center w-32">
                            <input
                                v-model="form.repetition_mode"
                                value="date"
                                class="mr-4"
                                type="radio"
                            />
                            Endet am:
                        </div>
                        <Input
                            :disabled="
                                form.repetition === 'once' ||
                                form.repetition_mode !== 'date'
                            "
                            v-model="form.repetition_value_date"
                            class="flex-1"
                            type="date"
                        />
                    </div>
                </div>
                <div
                    class="mt-2 text-red-600 text-sm absolute -bottom-6"
                    v-if="errors.repetition_value_amount"
                >
                    {{ errors.repetition_value_amount }}
                </div>
                <div
                    class="mt-2 text-red-600 text-sm absolute -bottom-6"
                    v-if="errors.repetition_value_date"
                >
                    {{ errors.repetition_value_date }}
                </div>
            </div>
            <div class="">
                <Label> Termindauer </Label>

                <Select
                    id="duration"
                    class="mt-1 block w-full"
                    v-model="form.duration"
                    required
                >
                    <option disabled value="">Dauer auswählen</option>

                    <option v-for="n in 10" :key="n" :value="n">
                        <!-- {{ n * interval }} Min. -->
                        {{ `${n} Einheiten` }} - {{ n * interval }} Min.
                    </option>
                </Select>
                <div class="mt-2 text-red-600 text-sm" v-if="errors.duration">
                    {{ errors.duration }}
                </div>
            </div>
        </div>
    </SettingsLayout>
</template>
